import * as firebase from 'firebase'
import _ from 'lodash'

import QueryBuilder from './QueryBuilder'

const InvoicesRepository = {

  createNewInvoice: (teamId, invoice, items, photos, referencedEvents) => {
    const updates = {}

    // Create invoice entity
    const invoicesPath = `invoices/${teamId}`
    const invoicesRef = firebase.database().ref(invoicesPath)
    const invoiceDbKey = invoicesRef.push().key
    invoice.id = invoiceDbKey
    updates[`${invoicesPath}/${invoiceDbKey}`] = invoice

    // Create invoice items entities    
    const invoiceItemsPath = `invoice_items/${teamId}/${invoice.id}`
    const invoiceItemsRef = firebase.database().ref(invoiceItemsPath)
    for (const item of items) {
      const itemDbKey = invoiceItemsRef.push().key
      item.id = itemDbKey
      item.invoiceId = invoice.id
      updates[`${invoiceItemsPath}/${itemDbKey}`] = item
    }

    // Create invoice photos entities
    const invoicePhotosPath = `invoice_photos/${teamId}/${invoice.id}`
    const invoicePhotosRef = firebase.database().ref(invoicePhotosPath)
    for (const photo of photos) {
      const itemDbKey = invoicePhotosRef.push().key
      photo.id = itemDbKey
      photo.invoiceId = invoice.id
      updates[`${invoicePhotosPath}/${itemDbKey}`] = photo
    }

    // Create referenced events entities
    const invoiceReferencedEventsPath = `invoice_events/${teamId}/${invoice.id}`
    const invoiceReferencedEventsRef = firebase.database().ref(invoiceReferencedEventsPath)
    for (const eventInfo of referencedEvents) {
      const itemDbKey = invoiceReferencedEventsRef.push().key
      eventInfo.id = itemDbKey
      eventInfo.invoiceId = invoice.id
      updates[`${invoiceReferencedEventsPath}/${itemDbKey}`] = eventInfo
    }
    
    return firebase.database().ref().update(updates)
  },

  updateInvoiceTotal: (teamId, invoice, total) => {
    const invoiceTotalPath = `invoices/${teamId}/${invoice.id}/total`
    firebase.database().ref(invoiceTotalPath).set(total)
  },

  addInvoiceItem: (teamId, invoice, item) => {
    const invoiceItemsPath = `invoice_items/${teamId}/${invoice.id}`
    const invoiceItemsRef = firebase.database().ref(invoiceItemsPath)
    const itemDbKey = invoiceItemsRef.push().key
    item.id = itemDbKey
    item.invoiceId = invoice.id
    firebase.database().ref(`${invoiceItemsPath}/${itemDbKey}`).set(item)
  },

  addInvoicePhotos: (teamId, invoice, photos) => {
    const invoicePhotosPath = `invoice_photos/${teamId}/${invoice.id}`
    const invoicePhotosRef = firebase.database().ref(invoicePhotosPath)
    for (const photo of photos) {
      const itemDbKey = invoicePhotosRef.push().key
      photo.id = itemDbKey
      photo.invoiceId = invoice.id
      firebase.database().ref(`${invoicePhotosPath}/${itemDbKey}`).set(photo)
    }
  },

  deleteInvoiceItem: (teamId, invoice, item) => {
    const itemPath = `invoice_items/${teamId}/${invoice.id}/${item.id}`
    firebase.database().ref(itemPath).remove()
  },

  updateItem: (teamId, invoice, item) => {
    const itemPath = `invoice_items/${teamId}/${invoice.id}/${item.id}`
    firebase.database().ref(itemPath).set(item)
  },

  addInvoicesListListener: (teamId, filters, listener) => {
    const query = QueryBuilder.buildInvoicesListQuery(teamId, filters)
    return query.on('value', listener)
  },

  removeInvoicesListListener: (teamId, filters, listener) => {
    const query = QueryBuilder.buildInvoicesListQuery(teamId, filters)
    return query.off('value', listener)
  },

  addInvoiceListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoiceId}`)
      .on('value', listener)
  },

  removeInvoiceListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoiceId}`)
      .off('value', listener)
  },

  addInvoiceItemsListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoice_items/${teamId}/${invoiceId}`)
      .on('value', listener)
  },

  removeInvoiceItemsListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoice_items/${teamId}/${invoiceId}`)
      .off('value', listener)
  },

  addInvoicePhotosListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoice_photos/${teamId}/${invoiceId}`)
      .on('value', listener)
  },

  removeInvoicePhotosListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoice_photos/${teamId}/${invoiceId}`)
      .off('value', listener)
  },

  addCustomerListener: (teamId, customerId, listener) => {
    return firebase.database()
      .ref(`customers/${teamId}/${customerId}`)
      .on('value', listener)
  },

  removeCustomerListener: (teamId, customerId, listener) => {
    return firebase.database()
      .ref(`customers/${teamId}/${customerId}`)
      .off('value', listener)
  },

  updateInvoiceCustomerInfo: (teamId, invoice, customerInfo) => {
    const updates = {}
    const base = `invoices/${teamId}/${invoice.id}`
    updates[`${base}/customerId`] = customerInfo.customerId
    updates[`${base}/contactId`] = customerInfo.contactId
    updates[`${base}/customerName`] = customerInfo.customer && customerInfo.customer.name || ''
    firebase.database().ref().update(updates)
  },

  addInvoiceReferencedEventsListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoice_events/${teamId}/${invoiceId}`)
      .on('value', listener)
  },

  removeInvoiceReferencedEventsListener: (teamId, invoiceId, listener) => {
    return firebase.database()
      .ref(`invoice_events/${teamId}/${invoiceId}`)
      .off('value', listener)
  },

  addInvoiceReferencedEvent: (teamId, invoice, eventInfo) => {
    const invoiceEventsPath = `invoice_events/${teamId}/${invoice.id}`
    const invoiceEventsRef = firebase.database().ref(invoiceEventsPath)
    const eventDbKey = invoiceEventsRef.push().key
    eventInfo.id = eventDbKey
    eventInfo.invoiceId = invoice.id
    firebase.database().ref(`${invoiceEventsPath}/${eventDbKey}`).set(eventInfo)
  },

  removeInvoiceReferencedEvent: (teamId, invoice, eventInfo) => {
    const itemPath = `invoice_events/${teamId}/${invoice.id}/${eventInfo.id}`
    firebase.database().ref(itemPath).remove()
  },

  deleteInvoice: (teamId, invoice) => {
    const tasks = []
    tasks.push(firebase.database()
      .ref(`invoices/${teamId}/${invoice.id}`)
      .remove())
    tasks.push(firebase.database()
      .ref(`invoice_items/${teamId}/${invoice.id}`)
      .remove())
    tasks.push(firebase.database()
      .ref(`invoice_photos/${teamId}/${invoice.id}`)
      .remove())
    tasks.push(firebase.database()
      .ref(`invoice_events/${teamId}/${invoice.id}`)
      .remove())
    return Promise.all(tasks)
  },

  setInvoiceNumber: (teamId, invoice, number) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoice.id}/number`)
      .set(number)
  },

  setInvoiceTitle: (teamId, invoice, title) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoice.id}/title`)
      .set(title)
  },
  
  setInvoiceDate: (teamId, invoice, invoiceDate) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoice.id}/invoiceDate`)
      .set(invoiceDate)
  },

  setInvoiceNotes: (teamId, invoice, notes) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoice.id}/notes`)
      .set(notes)
  },

  setInvoicePaymentDueDate: (teamId, invoice, paymentDueDate) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoice.id}/paymentDueDate`)
      .set(paymentDueDate)
  },

  getLastInvoice: (teamId, onInvoiceValue) => {
    return firebase.database()
      .ref(`invoices/${teamId}`)
      .limitToLast(1)
      .once('value')
      .then(onInvoiceValue)
  },

  getInvoice: (teamId, invoiceId) => {
    return firebase.database()
      .ref(`invoices/${teamId}/${invoiceId}`)
      .once('value')
      .then(snapshot => snapshot && snapshot.val())
  },

  getInvoiceByNumber: (teamId, number) => {
    return firebase.database()
      .ref(`invoices/${teamId}`)
      .orderByChild('number')
      .equalTo(number)
      .once('value')
      .then(snapshot => {
        const value = snapshot && snapshot.val()
        const list = value && _.map(value)
        return list && list.length && list[0]
      })
  },

  updateInvoiceStatusOnEmailSent: (teamId, invoiceId) => {
    // If the invoice status is draft, set it to sent, do nothing otherwise.
    return InvoicesRepository.getInvoice(teamId, invoiceId)
      .then(invoice => {
        if (invoice.status === 'draft') {
          return firebase.database()
            .ref(`invoices/${teamId}/${invoiceId}/status`)
            .set('sent')
        }
        return Promise.resolve(0)
      })
  }

}

export default InvoicesRepository



// WEBPACK FOOTER //
// ./src/billing/infra/InvoicesRepository.js